import React, { useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 30,
        paddingBottom: 34,
    },
    clickableItem: {
        margin: '0 5px',
        color: theme.palette.grey[400],
        '&:hover': {
            cursor: 'pointer',
            color: theme.palette.grey[700],
        },
        '&$disabled': { color: theme.palette.grey[400], cursor: 'initial' },
    },
    navIcon: {
        fontSize: 20,
    },
    active: {
        color: theme.palette.grey[700],
        fontWeight: 600,
    },
    paginationInput: {
        maxWidth: 30,
        '&[type=number]': {
            '-moz-appearance': 'textfield',
        },
        '&::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '&::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
    },
    disabled: {},
}));

const CustomPagination = ({ currentPage, lastPage, changePage, loading }) => {
    const classes = useStyles();
    const [jumpToPage, setJumpToPage] = useState(1);
    const [showPaginationInput, setShowPaginationInput] = useState([false, false]);

    const handlePrevPageChange = () => {
        if (currentPage > 1) {
            changePage(currentPage - 1)();
        }
    };

    const handleNextPageChange = () => {
        if (currentPage < lastPage) {
            changePage(currentPage + 1)();
        }
    };

    const handleGoToPage = e => {
        if (e.key === 'Enter') {
            setShowPaginationInput(false);
            if (isNaN(parseInt(jumpToPage))) {
                return;
            } else if (parseInt(jumpToPage) > lastPage) {
                changePage(lastPage)();
            } else if (parseInt(jumpToPage) < 1) {
                changePage(1)();
            } else {
                changePage(parseInt(jumpToPage))();
            }
        } else if (
            ((e.which <= 90 && e.which >= 65) || e.key === '+' || e.key === '-') &&
            !e.ctrlKey
        ) {
            e.preventDefault();
        }
    };

    const handleJumpToPageChange = e => {
        const page = e.target.value;
        setJumpToPage(page);
    };

    const handleClickMorePages = place => e => {
        if (place === 'first') {
            setShowPaginationInput([true, false]);
        } else if (place === 'second') {
            setShowPaginationInput([false, true]);
        }
    };

    const handleMorePagesLostFocus = e => {
        setShowPaginationInput([false, false]);
    };

    const pageNumbers = useMemo(() => [...Array(lastPage).keys()].map(k => k + 1), [lastPage]);
    const displayedPages = useMemo(() => {
        const pages = [];
        pageNumbers.forEach(pn => {
            if (pn >= currentPage - 2 && pn <= currentPage + 2) {
                pages.push(pn);
            } else if (pn <= 2 || pn >= lastPage - 2) {
                pages.push(pn);
            } else {
                if (pages[pages.length - 1] !== '...') {
                    pages.push('...');
                }
            }
        });
        return pages;
    }, [pageNumbers, currentPage, lastPage]);

    return (
        <div className={classes.container}>
            <icons.ArrowLeft
                className={clsx({
                    [classes.clickableItem]: true,
                    [classes.navIcon]: true,
                    [classes.disabled]: currentPage === 1 || loading,
                })}
                onClick={handlePrevPageChange}
            />
            {displayedPages.map((k, index) => {
                if (k === '...') {
                    const first = !displayedPages.slice(0, index).includes('...');
                    const placeIndex = first ? 0 : 1;
                    return (
                        <span
                            key={index}
                            className={classes.clickableItem}
                            onClick={handleClickMorePages(first ? 'first' : 'second')}
                        >
                            {showPaginationInput[placeIndex] ? (
                                <input
                                    type="number"
                                    min={1}
                                    max={lastPage}
                                    className={classes.paginationInput}
                                    value={jumpToPage}
                                    onChange={handleJumpToPageChange}
                                    onKeyDown={handleGoToPage}
                                    onBlur={handleMorePagesLostFocus}
                                    autoFocus
                                />
                            ) : (
                                k
                            )}
                        </span>
                    );
                }
                return (
                    <span
                        className={clsx({
                            [classes.clickableItem]: true,
                            [classes.active]: k === currentPage,
                        })}
                        key={index}
                        onClick={changePage(k)}
                    >
                        {k}
                    </span>
                );
            })}
            <icons.ArrowRight
                className={clsx({
                    [classes.clickableItem]: true,
                    [classes.navIcon]: true,
                    [classes.disabled]: currentPage <= lastPage || loading,
                })}
                onClick={handleNextPageChange}
            />
        </div>
    );
};

export default CustomPagination;
